// Copyright (c) StrangeBee 2024

import { ICONS } from '@Constants/iconsApp';
import { PageDirection } from '@Constants/pagination';
import { useLocalMessage } from '@Helpers/locale';
import { LayoutPaginationOptions } from '@Types';
import { Button, Space } from 'antd';
import React, { forwardRef } from 'react';

import './PaginationBrowse.scss';

interface PaginationBrowseProps {
    paginationConfig?: LayoutPaginationOptions;
    count?: number;
    loading?: boolean;
}

export const PaginationBrowse = forwardRef<HTMLDivElement, PaginationBrowseProps>(
    ({ paginationConfig, count = 0, loading }, ref) => {
        const i = useLocalMessage();

        const absCount = Math.abs(count);

        const { itemsPerPage, page, onPageChange } = paginationConfig || {};

        if (page === undefined || !itemsPerPage || !count || !onPageChange) {
            return null;
        }

        const handleChange = (direction: PageDirection): void => {
            const countPage = Math.ceil(absCount / itemsPerPage) - 1;
            const newPage = Math.max(0, Math.min(page + direction, countPage));

            if (page !== newPage) {
                onPageChange(newPage);
                scrollToTop();
            }
        };

        const from = page * itemsPerPage;

        const to = (page + 1) * itemsPerPage;

        const processedCount = count < 0 ? `${Math.abs(count)}+` : count;

        const scrollToTop = () => {
            (ref as any)?.current?.scrollIntoView({
                behavior: 'smooth',
            });
        };

        return (
            <div className="pagination-browse">
                <Button
                    size="small"
                    onClick={(): void => handleChange(PageDirection.PREVIOUS)}
                    disabled={from === 0 || loading}
                    loading={loading}
                >
                    <Space>
                        <ICONS.LeftOutlined />
                        {i('list.pagination.previous')}
                    </Space>
                </Button>
                <span className="pagination-browse__count">
                    {from} - {to > absCount ? processedCount : to} {i('list.pagination.of')} {processedCount}
                </span>

                <Button
                    size="small"
                    onClick={(): void => handleChange(PageDirection.NEXT)}
                    disabled={to >= absCount || loading}
                    loading={loading}
                >
                    <Space>
                        {i('list.pagination.next')}
                        <ICONS.RightOutlined />
                    </Space>
                </Button>
            </div>
        );
    },
);
